import React from 'react';
import { Label, Icon, Flag } from 'semantic-ui-react';
import moment from 'moment';

import time_secs_format from 'xAppLib/helpers/time_secs_format';
import { WordleWordRes } from 'views/game/Wordle';

const STATS_CONFIG = {
	win: { label: 'Won', color: 'green', icon: 'trophy' },
	end: { label: 'Ended', color: 'orange', icon: 'flag checkered' },
	guess: { label: 'In Progress', color: 'blue', icon: 'hourglass half' },
	start: { label: 'Started', color: 'grey', icon: 'play' },
	open: { label: 'Opened', color: 'grey', icon: 'eye' },
};

/**
 * Displays a single game play entry (one player's attempt at a game)
 *
 * @param {Object} props
 * @param {Object} props.play - The game play data
 * @param {boolean} props.showPlayer - Show player name (default: true)
 * @param {boolean} props.showMoveTimes - Show time for each move (default: true)
 * @param {boolean} props.showStatus - Show status badge (default: false)
 * @param {boolean} props.showDate - Show date/time played (default: true)
 * @param {boolean} props.showTotalTime - Show total time (default: true)
 * @param {boolean} props.showMoveCount - Show number of moves (default: true)
 * @param {'list' | 'card' | 'compact'} props.layout - Display layout (default: 'list')
 * @param {string} props.className - Additional CSS classes
 */
const GamePlayEntry = ({
	play,
	showPlayer = true,
	showMoveTimes = true,
	showStatus = false,
	showDate = true,
	showTotalTime = true,
	showMoveCount = true,
	layout = 'list',
	className = '',
}) => {
	if (!play) return null;

	const playerName = play.by?.un || play.by?.n?.split(' ')[0] || 'anon';
	const playDate = play.tm?.seconds ? moment.unix(play.tm.seconds).format('D/M/YY H:mm') : null;
	const totalTime = play.mvs?.[play.mvs?.length - 1]?.t;
	const moveCount = play.mvs?.length || 0;
	const statInfo = STATS_CONFIG[play.stat] || { label: play.stat, color: 'grey', icon: 'circle' };

	// Format time helper
	const formatTime = (t) => time_secs_format(Math.round(t / 100) / 10, 1);

	// Render moves with optional times - matches original leaderboard format
	const renderMoves = () => (
		<>
			{play.mvs?.map((mv, i) => (
				<span key={i} className="letter-row" style={{ marginBottom: '1px' }}>
					<WordleWordRes word={mv.w} res={mv.r} />
					{showMoveTimes && <> &nbsp; ({formatTime(mv.t)}) &nbsp;</>}
				</span>
			))}
		</>
	);

	const playerDisp = _ => <>
			{play?.by?.cc && <Flag name={play.by.cc.toLowerCase()} />}
			{play?.by?.ct && <span className="text-xs text-gray-500">{play.by.ct} </span>}
			{playerName}
		</>

	// Card layout (for mobile)
	if (layout === 'card') {
		return (
			<div className={`p-3 bg-gray-50 rounded-lg border ${className}`}>
				<div className="flex justify-between items-start mb-2">
					{showStatus && (
						<Label color={statInfo.color} size="small">
							<Icon name={statInfo.icon} />
							{statInfo.label}
						</Label>
					)}
					{showPlayer && !showStatus && (
						<span className="font-medium">{playerDisp()}</span>
					)}
					{showDate && playDate && (
						<span className="text-sm text-gray-500">{playDate}</span>
					)}
				</div>

				{showPlayer && showStatus && (
					<div className="mb-2 font-medium">{playerDisp()}</div>
				)}

				<div className="flex justify-between items-center mb-2">
					{showMoveCount && <span className="font-medium">{moveCount} moves</span>}
					{showTotalTime && totalTime && (
						<span className="text-sm">Time: {formatTime(totalTime)}</span>
					)}
				</div>

				{play.mvs && (
					<div className="mt-2">
						{play.mvs.map((mv, i) => (
							<div key={i} className="mb-1 flex items-center gap-2">
								<WordleWordRes word={mv.w} res={mv.r} />
								{showMoveTimes && (
									<span className="text-xs text-gray-500">({formatTime(mv.t)})</span>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}

	// Compact layout (minimal info)
	if (layout === 'compact') {
		return (
			<div className={`flex flex-wrap items-center gap-2 ${className}`}>
				{showStatus && (
					<Label color={statInfo.color} size="mini">
						<Icon name={statInfo.icon} />
						{statInfo.label}
					</Label>
				)}
				{showMoveCount && <span>{moveCount} moves</span>}
				{showTotalTime && totalTime && <span>({formatTime(totalTime)})</span>}
				{renderMoves()}
			</div>
		);
	}

	// Default list layout - matches original leaderboard format
	return (
		<div className={className}>
			{showPlayer && <div style={{ fontWeight: 'bold' }}>{playerDisp()}</div>}
			<div>
				{showDate && playDate && <><b>{playDate}</b> &nbsp; / &nbsp;</>}
				{showMoveCount && <><b>{moveCount}</b> moves &nbsp; / &nbsp;</>}
				{showTotalTime && totalTime && <>total time: {formatTime(totalTime)} &nbsp;</>}
				{showStatus && (
					<Label color={statInfo.color} size="mini">
						<Icon name={statInfo.icon} />
						{statInfo.label}
					</Label>
				)}
				<br />
				{renderMoves()}
				<br />
			</div>
		</div>
	);
};

export { GamePlayEntry, STATS_CONFIG };
export default GamePlayEntry;
